import type { ChangeEvent, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ImagePlus, Trash2 } from 'lucide-react';
import {
  ActorAssignmentRow,
  ActorRow,
  SpaceSummary,
  api,
} from '../api';
import { MarkdownEditor } from '../components/MarkdownEditor';

const ACTOR_TYPES = [
  { value: 0, label: 'Generic' },
  { value: 1, label: 'Character' },
  { value: 2, label: 'Camera' },
  { value: 3, label: 'FX System' },
  { value: 4, label: 'Light' },
];

const ACTOR_STATUSES = [
  { value: 0, label: 'enabled' },
  { value: 1, label: 'disabled' },
];

interface EditDraft {
  introduction: string;
  imageUrl: string;
  tags: string;
  status: 0 | 1;
}

export function ActorCatalog(): ReactElement {
  const params = useParams<{ id?: string }>();
  const [actors, setActors] = useState<ActorRow[]>([]);
  const [assignments, setAssignments] = useState<
    Record<string, ActorAssignmentRow[]>
  >({});
  const [spaceTitle, setSpaceTitle] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = async (actorId: string): Promise<void> => {
    setDeletingId(actorId);
    try {
      await api.deleteActor(actorId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const reload = async (): Promise<void> => {
    if (!params.id) return;
    try {
      const [list, s] = await Promise.all([
        api.listActors(params.id),
        api.getSpace(params.id).catch((): SpaceSummary | null => null),
      ]);
      setActors(list);
      if (s) setSpaceTitle(s.title);
      // Fetch claims for each actor in parallel so the per-card list
      // shows the current claiming agents.
      const pairs = await Promise.all(
        list.map(async (a) => {
          const rows = await api.listActorAssignments(a.id);
          return [a.id, rows] as const;
        }),
      );
      setAssignments(Object.fromEntries(pairs));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to load');
    }
  };

  useEffect(() => {
    void reload();
  }, [params.id]);

  if (!params.id) return <p>Missing space id</p>;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">
          {spaceTitle || 'Space'} Actors
        </h1>
        <span className="text-xs text-slate-500">
          {actors.length} actors 
        </span>
      </div>
      {error && <p className="text-rose-400 text-sm">{error}</p>}
      {actors.length === 0 ? (
        <p className="text-slate-400 text-sm">
          No actors have been discovered yet. When a UE client binds to
          this Space, tagged actors will be auto-registered at runtime.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {actors.map((actor) => (
            <ActorCard
              key={actor.id}
              actor={actor}
              claims={assignments[actor.id] ?? []}
              isEditing={editingId === actor.id}
              isSaving={savingId === actor.id}
              isDeleting={deletingId === actor.id}
              onStartEdit={() => setEditingId(actor.id)}
              onCancelEdit={() => setEditingId(null)}
              onDelete={() => void handleDelete(actor.id)}
              onSave={async (draft) => {
                setSavingId(actor.id);
                try {
                  await api.updateActor(actor.id, {
                    introduction: draft.introduction || null,
                    image_url: draft.imageUrl || null,
                    tags: draft.tags
                      .split(',')
                      .map((t) => t.trim())
                      .filter(Boolean),
                    status: draft.status,
                  });
                  setEditingId(null);
                  await reload();
                } catch (err) {
                  setError(
                    err instanceof Error ? err.message : 'update failed',
                  );
                } finally {
                  setSavingId(null);
                }
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

interface ActorCardProps {
  actor: ActorRow;
  claims: ActorAssignmentRow[];
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (draft: EditDraft) => Promise<void>;
  onDelete: () => void;
}

function ActorCard({
  actor,
  claims,
  isEditing,
  isSaving,
  isDeleting,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
}: ActorCardProps): ReactElement {
  const [draft, setDraft] = useState<EditDraft>(() => ({
    introduction: actor.introduction ?? '',
    imageUrl: actor.imageUrl ?? '',
    tags: actor.tags.join(', '),
    status: actor.status === 1 ? 1 : 0,
  }));
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await api.uploadImage(file, 'actor_image');
      setDraft((prev) => ({ ...prev, imageUrl: result.url }));
    } catch {
      // silent — user sees the field didn't update
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <article className="bg-panel rounded p-4 text-sm space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold">{actor.name}</h3>
          <span className="text-xs text-slate-400">
            {ACTOR_TYPES[actor.actorType]?.label ?? actor.actorType}
          </span>
          <span
            className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 ${
              actor.status === 1
                ? 'bg-slate-700 text-slate-300'
                : 'bg-emerald-800 text-emerald-100'
            }`}
          >
            {ACTOR_STATUSES.find((s) => s.value === actor.status)?.label ??
              actor.status}
          </span>
        </div>
        {!isEditing && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onStartEdit}
              className="text-xs text-accent hover:underline"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={isDeleting}
              className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
            >
              {isDeleting ? '...' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      <div className="text-[10px] text-slate-600 font-mono truncate" title={actor.id}>
        {actor.id}
      </div>

      {(actor.classHint || actor.ueClass) && (
        <div className="text-xs text-slate-500">
          {[actor.classHint, actor.ueClass].filter(Boolean).join(' · ')}
        </div>
      )}

      {isEditing ? (
        <div className="space-y-3 pt-2 border-t border-slate-700">
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Image
            </label>
            <div className="flex items-center gap-3">
              {draft.imageUrl ? (
                <div className="relative group w-40 aspect-[1.618/1] rounded-md border border-slate-700 overflow-hidden bg-slate-900 shrink-0">
                  <img
                    src={draft.imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setDraft({ ...draft, imageUrl: '' })}
                    title="Remove image"
                    className="absolute top-0.5 right-0.5 p-0.5 rounded bg-slate-900/80 text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={`rounded-md border border-dashed border-slate-600 flex flex-col items-center justify-center gap-1 text-slate-500 hover:border-slate-400 hover:text-slate-300 disabled:opacity-50 transition-colors ${
                  draft.imageUrl ? 'w-10 h-10 rounded' : 'w-40 aspect-[1.618/1]'
                }`}
              >
                <ImagePlus
                  size={draft.imageUrl ? 16 : 20}
                />
                {!draft.imageUrl && (
                  <span className="text-[10px]">
                    {uploading ? '...' : 'Upload'}
                  </span>
                )}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              onChange={(e) => {
                void handleUpload(e);
              }}
              className="hidden"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={draft.tags}
              onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
              className="w-full bg-slate-900 border border-slate-700 rounded p-2"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Status
            </label>
            <select
              value={draft.status}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  status: (Number(e.target.value) === 1 ? 1 : 0) as 0 | 1,
                })
              }
              className="bg-slate-900 border border-slate-700 rounded p-2"
            >
              {ACTOR_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">
              Introduction
            </label>
            <MarkdownEditor
              value={draft.introduction}
              onChange={(next) => setDraft({ ...draft, introduction: next })}
              rows={4}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={isSaving}
              onClick={() => void onSave(draft)}
              className="bg-accent text-slate-900 rounded px-3 py-1.5 text-sm font-medium disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={onCancelEdit}
              className="text-sm text-slate-400 hover:text-slate-200 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {actor.imageUrl && (
            <img
              src={actor.imageUrl}
              alt={actor.name}
              className="rounded max-h-32 object-contain"
            />
          )}
          {actor.introduction && (
            <p className="text-slate-300 line-clamp-3">
              {actor.introduction}
            </p>
          )}
          {actor.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {actor.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-xs bg-slate-800 rounded px-2 py-0.5"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      <div className="pt-2 border-t border-slate-700">
        <div className="text-xs text-slate-400 mb-1">
          Claimed by {claims.length === 0 ? 'no agents' : `${claims.length} agent(s)`}
        </div>
        {claims.length > 0 ? (
          <ul className="flex flex-wrap gap-1">
            {claims.map((c) => (
              <li
                key={c.id}
                className="text-xs rounded px-2 py-0.5 bg-sky-700 text-sky-100"
                title={`claimed ${c.assigned_at}`}
              >
                {c.user_display_name ?? c.user_id.slice(0, 8)}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-slate-500 italic">
            No agent has claimed this actor yet. Agents claim via the{' '}
            <code className="text-slate-400">claim_actor</code> MCP tool.
          </p>
        )}
      </div>
    </article>
  );
}
